import { z } from 'zod';

/**
 * UK Vehicle Registration Mark (VRM) validation
 * Supports various UK formats:
 * - Current format: AB12 CDE
 * - Prefix format: A123 BCD
 * - Suffix format: ABC 123D
 * - Dateless format: ABC 123
 */
const isValidUkVrm = (value: string): boolean => {
  const v = value.toUpperCase();
  const current = /^[A-Z]{2}\d{2}[A-Z]{3}$/; // AB12CDE
  const prefix = /^[A-Z]\d{1,3}[A-Z]{3}$/; // A123BCD
  const suffix = /^[A-Z]{3}\d{1,3}[A-Z]$/; // ABC123D
  const dateless = /^([A-Z]{1,4}\d{1,4}|\d{1,4}[A-Z]{1,4})$/; // ABC123 / 123ABC
  return current.test(v) || prefix.test(v) || suffix.test(v) || dateless.test(v);
};

export const vrmSchema = z.preprocess(
  (val) => (typeof val === 'string' ? val.trim().replace(/\s+/g, '').toUpperCase() : val),
  z
    .string()
    .min(2, 'VRM must be at least 2 characters')
    .max(10, 'VRM must be at most 10 characters')
    .refine(
      (val: string) => isValidUkVrm(val),
      'Invalid UK VRM format (e.g., AB12 CDE, A123 BCD)'
    )
);

/**
 * PCN (Penalty Charge Notice) number validation
 * Typically alphanumeric, 5-20 characters
 */
export const pcnNumberSchema = z
  .string()
  .min(5, 'PCN number must be at least 5 characters')
  .max(20, 'PCN number must be at most 20 characters')
  .regex(/^[A-Z0-9-]+$/i, 'PCN number can only contain letters, numbers, and hyphens')
  .transform((val) => val.toUpperCase());

/**
 * Email validation (basic format)
 */
export const emailSchema = z
  .string()
  .email('Invalid email address')
  .min(3, 'Email must be at least 3 characters')
  .max(100, 'Email must be at most 100 characters')
  .toLowerCase();

/**
 * Amount validation (£0 - £10,000 range for PCN fines)
 */
export const amountSchema = z
  .number()
  .min(0, 'Amount cannot be negative')
  .max(10000, 'Amount cannot exceed £10,000')
  .finite('Amount must be a valid number');

/**
 * Date validation (not in future, not before 2000)
 */
export const dateSchema = z
  .string()
  .or(z.date())
  .refine((val) => {
    const date = typeof val === 'string' ? new Date(val) : val;
    return !isNaN(date.getTime());
  }, 'Invalid date format')
  .refine((val) => {
    const date = typeof val === 'string' ? new Date(val) : val;
    return date <= new Date();
  }, 'Date cannot be in the future')
  .refine((val) => {
    const date = typeof val === 'string' ? new Date(val) : val;
    return date >= new Date('2000-01-01');
  }, 'Date cannot be before 2000')
  .transform((val) => {
    const d = typeof val === 'string' ? new Date(val) : val;
    return d.toISOString().slice(0, 10);
  });

/**
 * Optional date schema (allows null/undefined)
 */
export const optionalDateSchema = z
  .string()
  .or(z.date())
  .nullable()
  .optional()
  .refine((val) => {
    if (!val) return true;
    const date = typeof val === 'string' ? new Date(val) : val;
    return !isNaN(date.getTime());
  }, 'Invalid date format')
  .refine((val) => {
    if (!val) return true;
    const date = typeof val === 'string' ? new Date(val) : val;
    return date <= new Date();
  }, 'Date cannot be in the future')
  .transform((val) => {
    if (!val) return null;
    const d = typeof val === 'string' ? new Date(val) : val;
    return d.toISOString().slice(0, 10);
  });

/**
 * Phone number validation (UK format)
 */
export const phoneSchema = z
  .string()
  .transform((val) => val.replace(/\s+/g, ''))
  .refine(
    (val) => /^(?:\+44[1-9]\d{9}|0[1-9]\d{9})$/.test(val),
    'Invalid UK phone number (e.g., +447123456789 or 07123456789)'
  )
  .optional();

/**
 * Postcode validation (UK format)
 */
export const postcodeSchema = z
  .string()
  .transform((val) => val.trim().toUpperCase().replace(/\s+/g, ' '))
  .refine(
    (val) => /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i.test(val),
    'Invalid UK postcode (e.g., SW1A 1AA)'
  )
  .transform((val) => val.replace(/\s/g, ''));

/**
 * Ticket Form Validation Schema
 */
export const ticketFormSchema = z.object({
  pcn_number: pcnNumberSchema,
  vehicle_id: z.string().uuid('Invalid vehicle ID').optional(),
  vrm: vrmSchema.optional(),
  issue_date: dateSchema,
  contravention_date: dateSchema,
  amount: amountSchema,
  location: z.string().min(3, 'Location must be at least 3 characters').max(200),
  authority: z.string().min(2, 'Authority must be at least 2 characters').max(100),
  contravention_code: z.string().max(10).optional(),
  stage: z.enum([
    'PCN Issued',
    'Notice to Owner',
    'Charge Certificate',
    'Order for Recovery',
    'Parking Charge Notice',
    'Notice to Keeper',
    'Notice to Driver',
    'Reminder',
    'Final Reminder',
    'Debt Recovery',
    'Letter Before Claim',
    'Court Claim'
  ]),
  status: z.enum(['pending_review', 'appeal_ready', 'appealed', 'rejected', 'paid', 'transferred']),
  notes: z.string().max(1000, 'Notes must be at most 1000 characters').optional(),
});

/**
 * Vehicle Form Validation Schema
 */
export const vehicleFormSchema = z.object({
  vrm: vrmSchema,
  make: z.string().min(2, 'Make must be at least 2 characters').max(50),
  model: z.string().min(1, 'Model must be at least 1 character').max(50),
  year: z
    .number()
    .int('Year must be a whole number')
    .min(1980, 'Year must be 1980 or later')
    .max(new Date().getFullYear() + 1, 'Year cannot be in the future'),
  color: z.string().min(3, 'Color must be at least 3 characters').max(30).optional(),
  vin: z
    .string()
    .length(17, 'VIN must be exactly 17 characters')
    .regex(/^[A-HJ-NPR-Z0-9]{17}$/i, 'Invalid VIN format')
    .optional(),
  mot_expiry: optionalDateSchema,
  tax_expiry: optionalDateSchema,
  notes: z.string().max(500, 'Notes must be at most 500 characters').optional(),
});

/**
 * Driver Form Validation Schema
 */
export const driverFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  email: emailSchema.optional(),
  phone: phoneSchema,
  license_number: z
    .string()
    .length(16, 'UK driving license must be exactly 16 characters')
    .regex(/^[A-Z]{5}\d{6}[A-Z0-9]{5}$/i, 'Invalid UK driving license format')
    .transform((val) => val.toUpperCase())
    .optional(),
  address: z.string().min(5, 'Address must be at least 5 characters').max(200).optional(),
  postcode: postcodeSchema.optional(),
  risk_score: z.number().min(0).max(100).optional(),
  notes: z.string().max(500, 'Notes must be at most 500 characters').optional(),
});

/**
 * Hire Form Validation Schema
 */
export const hireFormSchema = z
  .object({
    vehicle_id: z.string().uuid('Invalid vehicle ID'),
    driver_id: z.string().uuid('Invalid driver ID').optional(),
    driver_name: z.string().min(2, 'Driver name must be at least 2 characters').max(100),
    start_date: dateSchema,
    end_date: optionalDateSchema,
    agreement_signed: z.boolean(),
    signature_type: z.enum(['wet', 'electronic', 'none']),
    compliance_score: z.number().min(0).max(100).optional(),
    document_url: z.string().url('Invalid document URL').optional(),
    notes: z.string().max(500, 'Notes must be at most 500 characters').optional(),
  })
  .refine(
    (data) => {
      if (data.end_date) {
        const start = typeof data.start_date === 'string' ? new Date(data.start_date) : data.start_date;
        const end = typeof data.end_date === 'string' ? new Date(data.end_date) : data.end_date;
        return end >= start;
      }
      return true;
    },
    {
      message: 'End date must be after or equal to start date',
      path: ['end_date'],
    }
  );

/**
 * Type exports for TypeScript integration
 */
export type TicketFormData = z.infer<typeof ticketFormSchema>;
export type VehicleFormData = z.infer<typeof vehicleFormSchema>;
export type DriverFormData = z.infer<typeof driverFormSchema>;
export type HireFormData = z.infer<typeof hireFormSchema>;

/**
 * Validation helper function with detailed error messages
 */
export function validateForm<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: Record<string, string> } {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  }

  // Transform Zod errors into field-level error messages
  const errors: Record<string, string> = {};
  result.error.issues.forEach((err) => {
    const path = err.path.join('.');
    errors[path] = err.message;
  });

  return { success: false, errors };
}

/**
 * Specific validation functions for each form
 */
export const validateTicketForm = (data: unknown) => validateForm(ticketFormSchema, data);
export const validateVehicleForm = (data: unknown) => validateForm(vehicleFormSchema, data);
export const validateDriverForm = (data: unknown) => validateForm(driverFormSchema, data);
export const validateHireForm = (data: unknown) => validateForm(hireFormSchema, data);
